import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Organization, useCreateOrganization, useUpdateOrganization } from "@/hooks/useOrganizations";

interface OrganizationFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  organization?: Organization | null;
}

const OrganizationFormModal = ({ isOpen, onClose, organization }: OrganizationFormModalProps) => {
  const [name, setName] = useState("");
  const [type, setType] = useState<"persona_fisica" | "persona_moral">("persona_fisica");

  const { mutate: createOrganization, isPending: isCreating } = useCreateOrganization();
  const { mutate: updateOrganization, isPending: isUpdating } = useUpdateOrganization();

  useEffect(() => {
    if (organization) {
      setName(organization.name);
      setType(organization.type);
    } else {
      setName("");
      setType("persona_fisica");
    }
  }, [organization, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) return;

    if (organization) {
      updateOrganization(
        { id: organization.id, name, type },
        {
          onSuccess: () => {
            onClose();
          },
        }
      );
    } else {
      createOrganization(
        { name, type },
        {
          onSuccess: () => {
            onClose();
          },
        }
      );
    }
  };

  const isLoading = isCreating || isUpdating;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {organization ? "Editar Organización" : "Nueva Organización"}
            </DialogTitle>
            <DialogDescription>
              {organization
                ? "Modifica los datos de la organización"
                : "Completa los datos para crear una nueva organización"}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Nombre</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nombre de la organización"
                required
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="type">Tipo de Organización</Label>
              <Select value={type} onValueChange={(value: "persona_fisica" | "persona_moral") => setType(value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona el tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="persona_fisica">Persona Física</SelectItem>
                  <SelectItem value="persona_moral">Persona Moral</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Guardando..." : organization ? "Actualizar" : "Crear"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default OrganizationFormModal;
